import * as React from "react";
import { repository } from "clientInstance";
import {
    DashboardResource,
    ProgressionResource,
    ProjectResource,
    ReleaseResource,
    DeploymentTemplateResource,
    TenantedDeploymentMode,
} from "client/resources";
import DashboardDataCube from "./DashboardDataCube";
import ProgressionDataCube from "./ProgressionDataCube";
import { DashboardFilters, DataCube, DimensionTypes } from "./DataCube";
import * as tenantTagsets from "components/tenantTagsets";
import {DataBaseComponent, DataBaseComponentState, Errors} from "components/DataBaseComponent/DataBaseComponent";
import {ChannelResource} from "../../../../client/resources/channelResource";
import {LifecycleResource} from "../../../../client/resources/lifecycleResource";
import DashboardConfiguration from "areas/dashboard/DashboardConfiguration/DashboardConfiguration";

interface DashboardDataSourceProps {
    project?: ProjectResource;
    filters: DashboardFilters;
    render(props: DashboardDataSourceState): JSX.Element;
}

interface DashboardDataSourceState extends DataBaseComponentState {
    projectLimit?: number;
    cube?: DataCube;
    isLoading: boolean;
    usesDashboard: boolean;
    hasInitialLoaded: boolean;
}

class DashboardDataSource extends DataBaseComponent<DashboardDataSourceProps, DashboardDataSourceState> {
    refreshFunction: (data?: any) => Promise<DataCube>;
    timeout: number;
    updateFlag: boolean = false;
    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: false,
            errors: null,
            usesDashboard: false,
            hasInitialLoaded: false
        };
    }

    async loadDashboardData(): Promise<DashboardResource> {
        const args = this.props.project ?
            { projectId: this.props.project.Id, showAll: true } :
            { highestLatestVersionPerProjectAndEnvironment: true };

        const releaseId = this.filteredRelease();
        if (releaseId) {
            (args as any)["releaseId"] = releaseId;
        }
        return repository.Dashboards.getDashboard(args);
    }

    loadProgressionData() {
        return repository.Progression.getProgression(this.props.project);
    }

    async loadChannels() {
        const result = await repository.Projects.getChannels(this.props.project);
        return result.Items;
    }

    async loadReleases(): Promise<ReleaseResource[]> {
        const result = await repository.Projects.getReleases(this.props.project);
        return result.Items;
    }

    filteredRelease() {
        if (this.props.filters[DimensionTypes.Release]) {
            return Object.keys(this.props.filters[DimensionTypes.Release])[0];
        }
        return null;
    }

    async loadPromotionData(releasesLoader: Promise<ReleaseResource[]>): Promise<DeploymentTemplateResource> {
        const releaseId = this.filteredRelease();
        if (!releaseId) {
            return null;
        }

        const releases = await releasesLoader;
        let release = releases.find(r => r.Id === releaseId);

        if (!release) {
            release = await repository.Releases.get(releaseId);
        }
        return repository.Releases.getDeploymentTemplate(release);
    }

    async setupMainDashboard() {
        const dashboardData = await this.loadDashboardData();

        this.refreshFunction = async (data: DashboardResource) => {
            if (!data) {
                data = await this.loadDashboardData();
            }
            return new DashboardDataCube(data, data.Tenants, [], [], null, [], []);
        };

        const cube = await this.refreshFunction(dashboardData);
        this.setState({ usesDashboard: true, cube, projectLimit: dashboardData.ProjectLimit || 200 });
    }

    async loadLifecycles(channelsAsync: Promise<ChannelResource[]>): Promise<LifecycleResource[]> {
        const channels = await channelsAsync;
        const lifecycleIds = channels.map(channel => channel.LifecycleId);
        const lifecycles = await repository.Lifecycles.allById(lifecycleIds);
        return Object.keys(lifecycles).map(id => lifecycles[id]);
    }

    async listTenantsWithMissingVariables(): Promise<string[]> {
        const missingVariables = await repository.Tenants.missingVariables({projectId: this.props.project.Id}, false);
        return missingVariables.map(mv => mv.TenantId);
    }

    async setupTenantedProjectDashboard() {
        const releaseId = this.filteredRelease();
        const releaseLoader = this.loadReleases();
        const dashboardLoader = this.loadDashboardData();
        const channelsLoader = this.loadChannels();

        const [dashboardData, channels, releases, promotions, tagSets, lifecycles, missingVariableTenants] =
            await Promise.all([
                dashboardLoader,
                channelsLoader,
                releaseLoader,
                this.loadPromotionData(releaseLoader),
                tenantTagsets.getAll(),
                this.loadLifecycles(channelsLoader),
                this.listTenantsWithMissingVariables()]);

        this.refreshFunction = async (data) => {
            if (!data) {
                data = await this.loadDashboardData();
            }
            return new DashboardDataCube(data, data.Tenants, releases, channels, promotions, tagSets, lifecycles, this.props.project, releaseId, missingVariableTenants);
        };

        const cube = await this.refreshFunction(dashboardData);
        this.setState({ usesDashboard: true, cube });
    }

    async setupProjectDashboard() {
        const [progressionData, channels] = await Promise.all([this.loadProgressionData(), this.loadChannels()]);

        this.refreshFunction = async (data) => {
            if (!data) {
                data = await this.loadProgressionData();
            }
            return new ProgressionDataCube(data as ProgressionResource, channels, this.props.project);
        };

        const cube = await this.refreshFunction(progressionData);
        this.setState({ usesDashboard: false, cube });
    }

    async setupDashboard() {
        return this.doBusyTask(async () => {
            this.setState({isLoading: true});
            try {
                if (!this.props.project) {
                    await this.setupMainDashboard();
                } else if (this.props.project.TenantedDeploymentMode !== TenantedDeploymentMode.Untenanted) {
                    await this.setupTenantedProjectDashboard();
                } else {
                    await this.setupProjectDashboard();
                }
            } finally {
                this.setState({isLoading: false, hasInitialLoaded: true});
            }
        });
    }

    async componentDidMount() {
        await this.setupDashboard();
        await this.beginRefreshLoop(() => this.refreshCube(), 6000);
    }

    refreshCube = async () => {
        const cube = await this.refreshFunction();
        return { cube };
    }

    componentDidUpdate() {
        if (this.updateFlag) {
            this.updateFlag = false;
            this.setupDashboard();
        }
    }

    shouldComponentUpdate(nextProps: DashboardDataSourceProps, nextState: DashboardDataSourceState) {
        if (this.props.project !== nextProps.project) {
            this.updateFlag = true;
            return false;
        }

        if (this.state.usesDashboard && this.doesFilterRequireReload(nextProps)) {
            this.updateFlag = true;
            return false;
        }

        return true;
    }

    doesFilterRequireReload(nextProps: DashboardDataSourceProps) {
        return nextProps.filters[DimensionTypes.Release] !== this.props.filters[DimensionTypes.Release];
    }

    render() {
        const { render } = this.props;
        return render(this.state);
    }
}

export default DashboardDataSource;
export { DashboardDataSourceState };


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/areas/projects/components/DashboardDataSource/DashboardDataSource.tsx